import {
  ApplicationProductTypeEnum,
  CustomerApplicationStatusEnum,
  Query,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Redirect,
  Stack,
  useFocusEffect,
  useRouter,
} from 'expo-router';
import { isQualified } from 'features/advance/helpers/AdvanceProducts.helpers';
import useSubmitEmploymentInfo from 'features/advance/hooks/useSubmitEmploymentInfo';
import useWhereToGo from 'features/advance/hooks/useWhereToGo';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import FetchEmploymentTypes from 'services/graphql/queries/EmploymentTypes';
import EmploymentInfoForm from 'shared/components/EmploymentInfoForm/EmploymentInfoForm';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import useAgreements from 'shared/hooks/useAgreements/useAgreements';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { getUuid } from 'shared/utils/getUuid/getUuid';
import { useQuery } from 'urql';

function EmploymentInfo() {
  const router = useRouter();
  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'employmentInfo',
  });

  const [{ data, fetching, error }] = useQuery<Query>({
    query: FetchEmploymentTypes,
    requestPolicy: 'cache-and-network',
  });

  const {
    advanceProducts,
    fetching: fetchingAdvanceProducts,
    error: errorAdvanceProducts,
  } = useWhereToGo();
  const hasActiveAdvanceAccount = isQualified(advanceProducts)
    ? advanceProducts.hasActiveAdvanceAccount
    : undefined;

  const { refreshTiles, fetching: fetchingHomeTiles } = useRefreshHomeTiles([
    HomeTileTypeIds.LINKED_ACCOUNTS_TILE,
    HomeTileTypeIds.EXTERNAL_ACCOUNTS_TILE,
    HomeTileTypeIds.LIST_ITEM_MANAGE_EXTERNAL_ACCOUNTS,
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_FOR_YOU,
    HomeTileTypeIds.ADVANCE_ACCOUNT_CARD_SQUARE_TILE,
    HomeTileTypeIds.VXL_SIDE_BY_SIDE_TILE_ACCOUNT_CARD,
  ]);

  const dismissToTab = useDismissToRootTab();

  const {
    agreementsListDetails: agreementsData,
    errorModel: agreementsError,
    loading: agreementsFetching,
  } = useAgreements([ApplicationProductTypeEnum.AdvanceV2]);

  const {
    isMissingAgreements,
    advanceStatus,
    submitAdvanceAccountApplication,
    fetching: fetchingSubmitStatus,
    employmentInfoError,
  } = useSubmitEmploymentInfo();

  useFocusEffect(
    useCallback(() => {
      if (fetchingAdvanceProducts || errorAdvanceProducts) {
        return;
      }
    }, [fetchingAdvanceProducts, errorAdvanceProducts]),
  );

  useEffect(() => {
    refreshTiles();
  }, [refreshTiles]);

  useEffect(() => {
    if (advanceStatus && !fetchingSubmitStatus && !fetchingHomeTiles) {
      switch (advanceStatus) {
        case CustomerApplicationStatusEnum.Approved:
          router.replace('/varo-advance/approved');
          break;
        case CustomerApplicationStatusEnum.InReview:
        case CustomerApplicationStatusEnum.Incomplete:
        case CustomerApplicationStatusEnum.ReadyToSubmit:
        case CustomerApplicationStatusEnum.Submitted:
        case CustomerApplicationStatusEnum.Unknown:
        case CustomerApplicationStatusEnum.Declined:
          router.push({
            pathname: '/varo-advance/error',
            params: {
              subcategory: 'va_application',
              error_type: advanceStatus,
              error_description: employmentInfoError?.message,
              step_name: 'employment_info',
            },
          });
          break;
        case CustomerApplicationStatusEnum.Pending:
          router.push('/varo-advance/account-pending');
          break;
      }
    } else if (employmentInfoError || isMissingAgreements) {
      router.push({
        pathname: '/varo-advance/error',
        params: {
          subcategory: 'va_application',
          error_type: employmentInfoError
            ? 'Error submitting employment info'
            : 'Missing Agreements',
          error_description: employmentInfoError?.message,
          step_name: 'employment_info',
        },
      });
    }
  }, [
    advanceStatus,
    fetchingSubmitStatus,
    employmentInfoError,
    isMissingAgreements,
    fetchingHomeTiles,
    router,
  ]);

  const employmentTypes = data?.employmentTypes;
  const allAgreementIds = agreementsData?.reduce<string[]>(
    (acc, agreement) => [...acc, agreement.id],
    [],
  ) ?? [''];

  const isFetchingData =
    fetching || agreementsFetching || fetchingAdvanceProducts;
  const hasError = error || agreementsError;
  const hasData = !!employmentTypes;

  const handleSubmit = async (formValues: {
    employmentStatus: string;
    monthlyIncome: string;
    tAndC: boolean;
  }) => {
    const employmentType = employmentTypes?.find(
      (type) => type.name === formValues.employmentStatus,
    );

    if (!employmentType) {
      return null;
    }
    const monthlyIncomeValue = formValues.monthlyIncome.replace(/[^0-9.]/g, ''); // Remove non-numeric characters from monthlyIncome to pass it as pure number

    await submitAdvanceAccountApplication({
      input: {
        employmentTypeId: employmentType?.id,
        monthlyIncome: {
          amount: monthlyIncomeValue,
          currency: 'USD',
        },
        agreements: allAgreementIds,
      },
      idempotencyId: getUuid(),
    });
  };

  const goBack = () => {
    dismissToTab(Tabs.HOME);
  };

  useBackHandler(goBack);

  if (hasActiveAdvanceAccount && !fetchingSubmitStatus && !advanceStatus) {
    return <Redirect href="/varo-advance/new" />;
  }

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: !hasError,
          gestureEnabled: false,
          title: '',
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar primaryOnPress={goBack} {...props} />
          ),
        }}
      />

      {isFetchingData || hasError ? (
        <View style={styles.errorContainer}>
          <ScreenTransitionOverlay
            isLoading={isFetchingData}
            LoadingComponent={<LoadingSpinner />}
            ErrorComponent={
              hasError ? (
                <ErrorScreen
                  screenType={ErrorScreenType.Error}
                  messageHeadline={translatedText('errorTitle')}
                  messageBody={translatedText('errorBody')}
                  primaryButton={{
                    title: translatedText('errorButton'),
                    onPress: () => {
                      goBack();
                    },
                  }}
                />
              ) : null
            }
          />
        </View>
      ) : null}

      {hasData && !isFetchingData ? (
        <View style={styles.subContainer}>
          <EmploymentInfoForm
            employmentTypes={employmentTypes}
            pdfType={ApplicationProductTypeEnum.AdvanceV2}
            handleSubmit={handleSubmit}
            fetchingStatus={fetchingSubmitStatus}
            showButtons
          />
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  errorContainer: {
    flex: 1,
  },
  subContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    paddingHorizontal: theme.spacing.m,
  },
});

export default EmploymentInfo;
